import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import { useUnclaimedAccess } from '@/context/ImpersonationContext';
import { BELOW_CHROME_HEIGHT } from '@/components/GlobalHeader';
import { FINANCES_PATHS } from '@/lib/financesPaths';
import {
  prefetchUnclaimedPageData,
  unclaimedKeys,
  type AcknowledgementStatus,
} from '@/hooks/useUnclaimed';

interface FinancesNavItem {
  label: string;
  path: string;
}

interface FinancesSidebarProps {
  /**
   * AR-316: render for the mobile nav drawer — always expanded, full width,
   * no collapse control, taller rows for touch.
   */
  inDrawer?: boolean;
  /** Called after a nav row navigates, so the drawer can close itself. */
  onNavigated?: () => void;
}

/**
 * AR-210 (TC-23.1, TC-25.2): each Finances route mounts its own layout, so the
 * sub-nav button that was just activated from the keyboard is destroyed and
 * focus falls to <body>; the next Enter then does nothing. Remember a keyboard
 * activation across the remount and put focus back on the (new) current item.
 */
let refocusSubnavAfterNavigation = false;

export const FinancesSidebar: React.FC<FinancesSidebarProps> = ({
  inDrawer = false,
  onNavigated,
}) => {
  const [location, navigate] = useLocation();
  const subnavRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (inDrawer || !refocusSubnavAfterNavigation) return;
    refocusSubnavAfterNavigation = false;
    subnavRef.current?.querySelector<HTMLElement>('[aria-current="page"]')?.focus({ preventScroll: true });
  }, [location, inDrawer]);
  const queryClient = useQueryClient();
  const { isEnabled: isResolvedEnabled } = useResolvedFeatureFlags();
  const hasUnclaimedAccess = useUnclaimedAccess();
  const [collapsed, setCollapsed] = useState(false);

  const prefetchUnclaimed = useCallback(() => {
    if (
      !isResolvedEnabled('unclaimedCommission') ||
      !hasUnclaimedAccess
    ) {
      return;
    }
    const ack = queryClient.getQueryData<AcknowledgementStatus>(
      unclaimedKeys.acknowledgement,
    );
    if (ack?.acknowledged) {
      void prefetchUnclaimedPageData(queryClient);
    }
  }, [isResolvedEnabled, queryClient, hasUnclaimedAccess]);

  const navItems = useMemo<FinancesNavItem[]>(() => {
    const items: FinancesNavItem[] = [
      { label: 'Invoices', path: FINANCES_PATHS.invoices },
      { label: 'Snapshot', path: FINANCES_PATHS.snapshots },
      { label: 'Statements', path: FINANCES_PATHS.statements },
    ];

    if (isResolvedEnabled('unclaimedCommission') && hasUnclaimedAccess) {
      items.push({ label: 'Unclaimed', path: FINANCES_PATHS.unclaimed });
    }

    return items;
  }, [isResolvedEnabled, hasUnclaimedAccess]);

  const isActive = (path: string) =>
    location === path || location.startsWith(path + '/');

  // Inside the mobile drawer the Sheet owns the height.
  const sidebarHeight = inDrawer
    ? undefined
    : BELOW_CHROME_HEIGHT;

  if (collapsed && !inDrawer) {
    return (
      <div
        data-app-chrome
        className="flex w-8 flex-col items-center border-r border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 pt-4"
        style={{ height: sidebarHeight }}
      >
        <button
          onClick={() => setCollapsed(false)}
          className="rounded p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          title="Expand Finances sidebar"
          aria-label="Expand Finances sidebar"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      data-app-chrome
      className={cn(
        'flex flex-col bg-gray-50/50 dark:bg-gray-900/50',
        inDrawer ? 'w-full' : 'w-56 border-r border-gray-200 dark:border-gray-700',
      )}
      style={{ height: sidebarHeight }}
    >
      {/* Header — collapse chevron + title */}
      <div className={cn('px-3 pb-2 space-y-3', inDrawer ? 'pt-3' : 'pt-4')}>
        <div className="flex items-center gap-1.5">
          {!inDrawer && (
            <button
              onClick={() => setCollapsed(true)}
              className="rounded p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          {/* AR-210 (TC-01.3): a label, not a heading, so it no longer outranks the page h1. */}
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 tracking-tight">
            Finances
          </p>
        </div>

        {/* + Invoice action button */}
        <button
          onClick={() => {
            navigate(FINANCES_PATHS.invoicesNew);
            onNavigated?.();
          }}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors',
            inDrawer ? 'min-h-11 py-2.5' : 'py-2',
            isActive(FINANCES_PATHS.invoicesNew)
              ? 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-750 hover:border-gray-300 dark:hover:border-gray-600',
          )}
        >
          <Plus className="h-4 w-4" />
          <span>Invoice</span>
        </button>
      </div>

      {/* Navigation links — text only (secondary menu standard) */}
      <nav
        aria-label="Finances"
        className={cn(
          'px-3 py-2 space-y-0.5',
          !inDrawer && 'flex-1 overflow-y-auto',
        )}
        ref={subnavRef}
      >
        {navItems.map((item) => {
          const active = isActive(item.path);

          return (
            <button
              key={item.path}
              onClick={(event) => {
                // detail === 0: activated with Enter/Space, not a pointer click.
                // Only when the location actually changes: Enter on the current item
                // would leave the flag set for a later pointer navigation.
                if (!inDrawer && event.detail === 0 && item.path !== location) refocusSubnavAfterNavigation = true;
                navigate(item.path);
                onNavigated?.();
              }}
              onMouseEnter={
                item.path === FINANCES_PATHS.unclaimed
                  ? prefetchUnclaimed
                  : undefined
              }
              onFocus={
                item.path === FINANCES_PATHS.unclaimed
                  ? prefetchUnclaimed
                  : undefined
              }
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex w-full items-center rounded-lg px-3 text-sm text-left transition-colors',
                inDrawer ? 'min-h-11 py-2.5' : 'py-2',
                active
                  ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 font-medium'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200',
              )}
            >
              {item.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default FinancesSidebar;
